import { ethers } from 'ethers'
import { UNI_SWAP_ROUTER02 } from '../addresses'
import { UNI_SWAP_ROUTER02_ABI } from '../abi'
import { candidateV3Path, poolFeesOrSpacings } from '../path'
import type { Adapter, ExecuteArgs } from './types'
import type { RouteCandidate } from '../types'

/**
 * Uniswap v3 through SwapRouter02.
 *
 * Two sentinel addresses decide where the tokens land, and they were read from
 * the deployed contract's own verified source rather than remembered:
 * `contracts/libraries/Constants.sol` of `0x2626664c…` on Base (Sourcify,
 * runtime match, chain 8453) says
 *
 *     address internal constant MSG_SENDER   = address(1);
 *     address internal constant ADDRESS_THIS = address(2);
 *
 * and `V3SwapRouter.exactInputInternal` resolves both before it swaps. So a
 * swap that must end in native ETH sends its output to `ADDRESS_THIS` and then
 * unwraps it to the user in the same `multicall`; sending it to the router's
 * own literal address instead would leave the WETH there for anyone to sweep.
 *
 * The same source settles the other two things this adapter relies on:
 * `PeripheryPayments.pay` wraps `msg.value` itself when the token being paid
 * is WETH9, so a native-in swap needs no explicit wrap; and the deadline lives
 * on the `multicall(uint256, bytes[])` overload rather than in the params,
 * which is why every send here — even a single-call one — is wrapped in it.
 */
export const MSG_SENDER = '0x0000000000000000000000000000000000000001'
export const ADDRESS_THIS = '0x0000000000000000000000000000000000000002'

const iface = new ethers.utils.Interface(UNI_SWAP_ROUTER02_ABI as unknown as string[])

export const uniV3Adapter: Adapter = {
  spender(args: ExecuteArgs): string | null {
    return args.tokenIn.isNative ? null : UNI_SWAP_ROUTER02
  },

  async buildTx(args: ExecuteArgs): Promise<ethers.PopulatedTransaction> {
    const candidate = args.quote.candidate as RouteCandidate
    const amountIn = ethers.BigNumber.from(args.quote.amountIn.toString())
    const minOut = ethers.BigNumber.from(args.minOut.toString())
    /*
     * The router has to hold the WETH before it can unwrap it, so a swap that
     * ends in ETH names the router as the recipient and hands the proceeds to
     * the user in the next call of the same multicall.
     */
    const recipient = args.tokenOut.isNative ? ADDRESS_THIS : args.account

    const swapData =
      candidate.pools.length === 1
        ? iface.encodeFunctionData('exactInputSingle', [
            {
              tokenIn: candidate.path[0],
              tokenOut: candidate.path[1],
              fee: poolFeesOrSpacings(candidate.pools)[0],
              recipient,
              amountIn,
              amountOutMinimum: minOut,
              sqrtPriceLimitX96: 0
            }
          ])
        : iface.encodeFunctionData('exactInput', [
            {
              path: candidateV3Path(candidate),
              recipient,
              amountIn,
              amountOutMinimum: minOut
            }
          ])

    const calls: string[] = [swapData]
    if (args.tokenOut.isNative) {
      calls.push(iface.encodeFunctionData('unwrapWETH9', [minOut, args.account]))
    }
    if (args.tokenIn.isNative) {
      // Nothing should be left over, but a router holding somebody's dust is a
      // router somebody else empties.
      calls.push(iface.encodeFunctionData('refundETH', []))
    }

    return {
      to: UNI_SWAP_ROUTER02,
      data: iface.encodeFunctionData('multicall(uint256,bytes[])', [
        ethers.BigNumber.from(args.deadline.toString()),
        calls
      ]),
      value: args.tokenIn.isNative ? amountIn : ethers.constants.Zero
    }
  }
}
